import {
  isNilOrZero,
} from '@populateui/utils';
import {
  SAVE_DEBOUNCE_MS,
} from 'views/EMR/Sticky/stores/constants';

export type SaveQueue = {
  schedule: (stickyId: number) => void;
  cancel: (stickyId: number) => void;
  cancelAll: () => void;
  isInFlight: (stickyId: number) => boolean;
  markInFlight: (stickyId: number) => void;
  clearInFlight: (stickyId: number) => void;
  clearAllInFlight: () => void;
  clearTimerIfAny: (stickyId: number) => void;
};

type Options = {
  flush: (stickyId: number) => Promise<void>;
  debounceMs?: number;
};

export function createSaveQueue({
  flush,
  debounceMs: _debounceMs,
}: Options): SaveQueue {
  const debounceMs = _debounceMs ?? SAVE_DEBOUNCE_MS;
  const saveTimers = new Map<number, number>();
  const inFlight = new Set<number>(); // (i.e. currently being saved)

  function clearTimerIfAny(stickyId: number) {
    const existing = saveTimers.get(stickyId);
    if (!isNilOrZero(existing)) {
      window.clearTimeout(existing);
      saveTimers.delete(stickyId);
    }
  }

  function schedule(stickyId: number) {
    // reset debounce timer
    const existing = saveTimers.get(stickyId);
    if (!isNilOrZero(existing)) {
      window.clearTimeout(existing);
    }

    const timer = window.setTimeout(() => {
      flush(stickyId);
    }, debounceMs);

    saveTimers.set(stickyId, timer);
  }

  function cancel(stickyId: number) {
    clearTimerIfAny(stickyId);
    inFlight.delete(stickyId);
  }

  function cancelAll() {
    Array.from(saveTimers.values()).forEach((t) => window.clearTimeout(t));
    saveTimers.clear();
    inFlight.clear();
  }

  function isInFlight(stickyId: number) {
    return inFlight.has(stickyId);
  }

  function markInFlight(stickyId: number) {
    inFlight.add(stickyId);
  }

  function clearInFlight(stickyId: number) {
    inFlight.delete(stickyId);
  }

  function clearAllInFlight() {
    inFlight.clear();
  }

  return {
    schedule,
    cancel,
    cancelAll,
    isInFlight,
    markInFlight,
    clearInFlight,
    clearAllInFlight,
    clearTimerIfAny,
  };
}
